import React from "react";
import { FormGroup, Label, Input, Row, Col } from "reactstrap";
import routes from "../../routes/index";

const UserPermissions = ({ permissions = {}, setPermissions, role }) => {
  const filteredPermissions =
    role === "kitchen-cook"
      ? permissions?.orders
        ? { orders: permissions?.orders }
        : {}
      : role === "kitchen-admin"
      ? permissions
      : role === "kitchen-waiter"
      ? {
          ...(permissions?.orders && { orders: permissions.orders }),
          ...(permissions?.menus && {
            menus: {
              ...Object.fromEntries(
                Object.entries(permissions.menus).filter(
                  ([key]) => key !== "label" && key !== "addOns"
                )
              ),
              options: { get: true }, // keep only 'get' in menus.options
              category: {
                ...permissions.menus.category,
                options: { get: true },
                items: {
                  ...permissions.menus.category.items,
                  options: { get: true },
                },
              },
            },
          }),
        }
      : {};

  const findRouteByKey = (key) => {
    for (const group of routes) {
      if (group?.name?.toLowerCase() === key.toLowerCase()) {
        return group?.path;
      }
    }
    return null;
  };

  const clearChildrenRecursively = (obj) => {
    const newObj = { ...obj, active: false };
    if (newObj.options) {
      for (const opt in newObj.options) {
        newObj.options[opt] = false;
      }
    }

    for (const key in newObj) {
      if (
        typeof newObj[key] === "object" &&
        !Array.isArray(newObj[key]) &&
        key !== "options" &&
        key !== "active"
      ) {
        newObj[key] = clearChildrenRecursively(newObj[key]);
      }
    }

    return newObj;
  };

  const toggleTopLevelPermission = (path) => {
    updatePermissionAtPath(path, (perm = {}) => {
      const newActive = !perm.active;
      let updated = {
        ...perm,
        active: newActive,
      };

      const key = path.split(".").pop();

      if (newActive) {
        const matched = findRouteByKey(key);
        if (matched) {
          updated.path = matched;
        }
      }

      if (!newActive) {
        updated = clearChildrenRecursively(updated);
      }

      return updated;
    });
  };

  const toggleDetailedPermission = (path, option) => {
    if (option === "get") return;
    updatePermissionAtPath(path, (perm) => ({
      ...perm,
      options: {
        ...perm.options,
        [option]: !perm?.options?.[option],
        get: true,
      },
    }));
  };

  const updatePermissionAtPath = (path, updater) => {
    setPermissions((prev = {}) => {
      const segments = path.split(".");
      const last = segments.pop();
      const newObj = { ...prev };

      let temp = newObj;
      for (let i = 0; i < segments.length; i++) {
        const seg = segments[i];
        temp[seg] = { ...temp[seg] };
        temp = temp[seg];
      }

      temp[last] = updater(pointerAt(prev, path));
      return newObj;
    });
  };

  const pointerAt = (obj, path) => {
    return path.split(".").reduce((acc, key) => acc?.[key], obj);
  };

  const renderPermissions = (permObj, parentPath = "") => {
    return Object.entries(permObj).map(([key, value = {}]) => {
      if (!value || typeof value !== "object" || Array.isArray(value))
        return null;

      const currentPath = parentPath ? `${parentPath}.${key}` : key;
      const labelId = `top-${currentPath}`;
      const isActive = value?.active || false;
      const options = value?.options || {};

      const isPathActive = (path) => {
        const segments = path.split(".");
        let current = permissions;
        for (const segment of segments) {
          current = current?.[segment];
          if (!current?.active) return false;
        }
        return true;
      };

      const subPermissions = Object.entries(value)
        .filter(
          ([k, v]) =>
            typeof v === "object" &&
            v !== null &&
            !Array.isArray(v) &&
            k !== "options" &&
            k !== "key" &&
            k !== "active"
        )
        .reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {});

      return (
        <FormGroup key={currentPath} className="ms-2 mb-1">
          <Row className="align-items-center">
            <Col xs="8">
              <div className="form-check w-100 p-0 d-flex align-items-center">
                <Input
                  type="checkbox"
                  id={labelId}
                  className="form-check-input timing-modal-checkbox me-2"
                  checked={isActive}
                  onChange={() => toggleTopLevelPermission(currentPath)}
                />
                <Label
                  htmlFor={labelId}
                  className="form-check-label timing-modal-checkbox-label ml-2 my-1"
                >
                  {key.charAt(0).toUpperCase() + key.slice(1)}
                </Label>
              </div>
            </Col>
          </Row>

          {isActive && options && (
            <div className="pl-4 mt-1">
              {Object.entries(options).map(([option, isChecked]) => {
                if (option === "key") return null;

                const optionId = `opt-${currentPath}-${option}`;
                const isGet = option === "get";

                return (
                  <FormGroup key={option} check>
                    <Label check className="d-flex align-items-center mb-1">
                      <Input
                        type="checkbox"
                        id={optionId}
                        className="form-check-input timing-modal-checkbox"
                        checked={isGet ? true : !!isChecked}
                        disabled={isGet}
                        onChange={() => {
                          if (!isGet)
                            toggleDetailedPermission(currentPath, option);
                        }}
                      />
                      <span
                        className={`ml-2 mt-1 ${isGet ? "text-muted" : ""}`}
                      >
                        {option.charAt(0).toUpperCase() + option.slice(1)}
                      </span>
                    </Label>
                  </FormGroup>
                );
              })}
            </div>
          )}

          {Object.keys(subPermissions).length > 0 &&
            isPathActive(currentPath) && (
              <div className="pl-3 ml-2 border-start border-light ps-3 mt-2">
                {renderPermissions(subPermissions, currentPath)}
              </div>
            )}
        </FormGroup>
      );
    });
  };

  return (
    <div className="d-flex flex-column ml-3">
      {renderPermissions(filteredPermissions)}
    </div>
  );
};

export default UserPermissions;
